import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useGetPhotoTagRecommendationsQuery,
  useBulkAddPhotoTagsMutation,
  useBulkDeletePhotoTagsMutation
} from '@ports/internal-services/photoTag/photoTagSlice';
import { EditTagsModalProps } from '@business/interfaces/photo';
import { Button, Input } from '@business/workflows/ComponentUI';
import { SVGAssets } from '@business/assets/vectors';
import { useDebounce } from '@hooks/Debounce';

const EditTagsModal: React.FC<EditTagsModalProps> = ({
  isOpen,
  onClose,
  assetId,
  currentTags,
  onTagsUpdated
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<
    Array<{ id?: string; tagName: string }>
  >(currentTags || []);
  const [showRecommendations, setShowRecommendations] = useState(false);

  // Debounced search state
  const debouncedSearch = useDebounce(searchQuery);

  // RTK Query hooks (use debouncedSearch)
  const { data: recommendationsData } = useGetPhotoTagRecommendationsQuery(
    {
      assetId,
      search: debouncedSearch
    },
    {
      skip: !debouncedSearch || debouncedSearch.length < 2
    }
  );

  const [bulkAddTags, { isLoading: isAdding }] = useBulkAddPhotoTagsMutation();
  const [bulkDeleteTags, { isLoading: isDeleting }] =
    useBulkDeletePhotoTagsMutation();

  useEffect(() => {
    if (isOpen) {
      setSelectedTags(currentTags || []);
      setSearchQuery('');
      setShowRecommendations(false);
    }
  }, [isOpen, currentTags]);

  const recommendations = recommendationsData?.data || [];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    setShowRecommendations(value.length >= 2);
  };

  const handleAddTag = (tagName: string) => {
    if (
      !selectedTags.some(
        tag => tag.tagName.toLowerCase() === tagName.toLowerCase()
      )
    ) {
      setSelectedTags([...selectedTags, { tagName }]);
    }
    setSearchQuery('');
    setShowRecommendations(false);
  };

  const handleRemoveTag = (tagName: string) => {
    setSelectedTags(selectedTags.filter(tag => tag.tagName !== tagName));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && searchQuery.trim()) {
      e.preventDefault();
      handleAddTag(searchQuery.trim());
    }
  };

  const handleSave = async () => {
    try {
      // Check if there's an uncommitted tag in the input field
      const pendingTag = searchQuery.trim();
      let finalSelectedTags = selectedTags;

      if (
        pendingTag &&
        !selectedTags.some(
          tag => tag.tagName.toLowerCase() === pendingTag.toLowerCase()
        )
      ) {
        finalSelectedTags = [...selectedTags, { tagName: pendingTag }];
      }

      // Find tags to add (new tags without id)
      const tagsToAdd = finalSelectedTags
        .filter(tag => !tag.id)
        .map(tag => tag.tagName);

      // Find tags to delete (original tags not in selected)
      const tagsToDelete = currentTags
        .filter(tag => !finalSelectedTags.some(st => st.id === tag.id))
        .map(tag => tag.id);

      // Execute bulk operations
      if (tagsToAdd.length > 0) {
        await bulkAddTags({
          assetId,
          tags: tagsToAdd
        }).unwrap();
      }

      if (tagsToDelete.length > 0) {
        await bulkDeleteTags({
          tagIds: tagsToDelete,
          delete: false
        }).unwrap();
      }

      onTagsUpdated();
      onClose();
    } catch (error) {
      console.error('Error updating tags:', error);
    }
  };

  if (!isOpen) return null;

  const isLoading = isAdding || isDeleting;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-800">
            {t('mediaInfoSidebar.editTags')}
          </h2>
          <Button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
            aria-label="Close"
            btnStyle="no-outline">
            ×
          </Button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Search/Add Input */}
          <div className="mb-4 relative">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('mediaInfoSidebar.addTag')}
            </label>
            <Input
              type="text"
              value={searchQuery}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder={t('mediaInfoSidebar.searchOrAddTag')}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            />

            {/* Recommendations Dropdown */}
            {showRecommendations && recommendations.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                {recommendations.map((rec: any, index: number) => (
                  <Button
                    key={index}
                    onClick={() => handleAddTag(rec.tagName)}
                    btnStyle="no-outline"
                    className="w-full px-4 py-2 text-left hover:bg-gray-100 !justify-between">
                    <span className="text-gray-800">{rec.tagName}</span>
                    <span className="text-xs text-gray-500">{`(${rec.count})`}</span>
                  </Button>
                ))}
              </div>
            )}

            {/* Add new tag hint */}
            {showRecommendations &&
              searchQuery &&
              recommendations.length === 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
                  <Button
                    onClick={() => handleAddTag(searchQuery)}
                    btnStyle="no-outline"
                    className="w-full px-4 py-2 text-left hover:bg-gray-100">
                    <span className="text-gray-800">
                      {t('mediaInfoSidebar.addNewTag')} "{searchQuery}"
                    </span>
                  </Button>
                </div>
              )}
          </div>

          {/* Selected Tags */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('mediaInfoSidebar.selectedTags')}
            </label>
            {selectedTags.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {selectedTags.map((tag, index) => (
                  <span
                    key={tag.id || index}
                    className="px-3 py-1 bg-lime-200 text-gray-800 text-sm rounded-full flex items-center gap-2">
                    {tag.tagName}
                    <Button
                      onClick={() => handleRemoveTag(tag.tagName)}
                      className="hover:text-gray-900"
                      aria-label="Remove tag"
                      btnStyle="no-outline">
                      <SVGAssets.CloseBtn className="w-2 h-2" />
                    </Button>
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">
                {t('mediaInfoSidebar.noTagsSelected')}
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-4 border-t">
          <Button
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
            btnStyle="no-outline">
            {t('cancel')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={isLoading}
            className="flex-1 px-4 py-2 bg-[#49712B] text-white rounded-md hover:bg-[#49712B] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            btnStyle="no-outline">
            {t('save')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EditTagsModal;
